import { Helmet } from 'react-helmet-async';
import { Navigate, Route } from 'react-router-dom-v5-compat';

import { InquiryPreviewFromTemplate } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { VerificationTemplateVersionStubs } from 'dashboard/models/simulate/stubs';
import { InquiryTemplateVersionSchema } from 'dashboard/redux/slices/inquiry-preview';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { FieldValue } from 'lib/fields';
import useDebounced from 'lib/hooks/useDebounced';
import { useExpandCollapseNavbar } from 'lib/hooks/useExpandCollapseNavbar';
import { ApiModel } from 'lib/interfaces/api';

interface DashboardInquiryPreviewShowViewProps {
  inquiry: Inquiry | undefined;
  inquiryTemplateVersion: InquiryTemplateVersion | undefined;
  inquiryTemplate: InquiryTemplate | undefined;
  inquiryTemplateVersionSchema: InquiryTemplateVersionSchema | undefined;
  loadingSimulateInquiry: boolean;
  loadingInquiryTemplateAndAccountTypesData: boolean;
  accountTypesFilterOptions: AccountTypesFilterOption[] | undefined;
  currentEnvironmentType: Environment['type'];
  handleSaveVerificationTemplateVersionStubs: (
    inquiryId: string,
    verificationTemplateVersionStubs: VerificationTemplateVersionStubs[],
    fieldStubs: Record<string, FieldValue>,
  ) => Promise<void>;
  handleSkipToStep: (stepName: string, inquiryId: string) => Promise<void>;
  handleCreateSimulateInquiry: (props: {
    inquiryTemplateId: string;
    inquiryTemplateVersionId: string;
    referenceId?: string;
    accountTypeId?: string;
    fields?: Record<string, any>;
  }) => Promise<void>;
  handleLoadInquiry: (inquiryId: string, abortSignal?: AbortSignal) => Promise<void>;
  handleViewInquiry: () => void;
}

const DashboardInquiryTemplatePreviewShowView = ({
  inquiry,
  inquiryTemplateVersion,
  inquiryTemplate,
  inquiryTemplateVersionSchema,
  loadingSimulateInquiry,
  loadingInquiryTemplateAndAccountTypesData,
  accountTypesFilterOptions,
  currentEnvironmentType,
  handleSaveVerificationTemplateVersionStubs,
  handleSkipToStep,
  handleCreateSimulateInquiry,
  handleLoadInquiry,
  handleViewInquiry,
}: DashboardInquiryPreviewShowViewProps) => {
  const [debouncedHandleSaveVerificationTemplateVersionStubs] = useDebounced(
    handleSaveVerificationTemplateVersionStubs,
    500,
    {
      leading: false,
      trailing: true,
    },
  );

  const trackEvent = (eventName: string, props: Record<string, any> = {}) => {
    SegmentClient.sendSegmentTrackEvent(eventName, {
      'inquiry-template': inquiryTemplate?.id,
      'inquiry-id': inquiry?.id,
      ...props,
    });
  };

  useExpandCollapseNavbar();

  const nullStateBackRoute =
    inquiryTemplate && inquiryTemplateVersion
      ? `/dashboard/inquiry-templates/${inquiryTemplate.id}/${inquiryTemplateVersion.id}/flow`
      : 'dashboard/inquiry-templates';

  if (currentEnvironmentType !== ApiModel.EnvironmentSandbox) {
    return (
      <Backdrop>
        <NullState
          message='Preview is only available in Sandbox environments.'
          backRoute={nullStateBackRoute}
        />
      </Backdrop>
    );
  } else if (loadingInquiryTemplateAndAccountTypesData) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    );
  } else if (
    inquiryTemplate == null ||
    inquiryTemplateVersion == null ||
    inquiryTemplateVersionSchema == null
  ) {
    return (
      <Backdrop>
        <NullState
          message='We couldn’t find the Template to preview.'
          backRoute={nullStateBackRoute}
        />
      </Backdrop>
    );
  }

  return (
    <>
      <Helmet title={`Preview - ${inquiryTemplate.attributes.name} - Templates - Inquiries`} />
      <SentryRoutes>
        <Route
          index
          element={
            <InquiryPreviewFromTemplate
              inquiryTemplate={inquiryTemplate}
              inquiryTemplateVersion={inquiryTemplateVersion}
              inquiry={inquiry}
              accountTypesFilterOptions={accountTypesFilterOptions}
              workflowVersionMap={inquiryTemplateVersionSchema.workflowVersions}
              verificationTemplateVersions={
                inquiryTemplateVersionSchema.verificationTemplateVersions
              }
              isLoadingInquiry={loadingSimulateInquiry}
              trackEvent={trackEvent}
              handleCreateSimulateInquiry={handleCreateSimulateInquiry}
              handleSkipToStep={handleSkipToStep}
              handleSaveVerificationTemplateVersionStubs={
                debouncedHandleSaveVerificationTemplateVersionStubs
              }
              handleViewInquiry={handleViewInquiry}
              handleLoadInquiry={handleLoadInquiry}
            />
          }
        />
        <Route path='*' element={<Navigate to='/dashboard/inquiry-templates' replace />} />
      </SentryRoutes>
    </>
  );
};

export default DashboardInquiryTemplatePreviewShowView;
